import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useMemo } from 'react'
import { useEffect } from 'react';
import { useState } from 'react';
import { Alert, Badge, Breadcrumb, Button, Card, Col, Container, Modal, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AssessmentStatus, ModelAssessmentResponse, ProcessType, Projects } from '../../api/Projects';
import AlidaLoader from '../../components/common/AlidaLoader';
import UserItem from '../../components/common/UserItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { formatDate } from '../../utils';
import { ProjectAsset } from '../../components/list-items/ProjectAsset';
import { Flex, Tooltip } from 'antd';
import { Project } from '../../models/Project';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppTag from '../../components/common/AppTag';
import { EventEmitter } from '../../utils/EventEmitter';
import { useInterval } from '../../utils/useInterval';
import { useAuth } from '../../contexts/auth';


export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { loggedUser } = useAuth();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [forcePolling, setForcePolling] = useState(true);
  const [project, setProject] = useState<Project>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showAssessmentDataModal, setShowAssessmentDataModal] = useState(false);
  const [error, setError] = useState(false);
  const [assessmentStatus, setAssessmentStatus] = useState<AssessmentStatus>();
  const [assessmentData, setAssessmentData] = useState<ModelAssessmentResponse>();

  const projectLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "projects"));
  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));
  const projectUseCase = useMemo(() => project?.tags?.find(tag => tag.toLowerCase().startsWith('use_case:'))?.replace(/use_case:/, ''), [project]);
  const TAIUrl = useMemo(() => {
    return project && projectUseCase
      ? `${window.__env.TAI_ASSESSMENT_BASE_URL}/asset_designer_page?assessment_id=${project.id}&use_case=${projectUseCase}`
      : null;
  }, [project, projectUseCase]);

  useEffect(() => {
    if (id) EventEmitter.dispatch('projectId', id);
  }, [id]);

  useEffect(() => {
    Projects.getProject(+id!)
      .then(
        ({ data }) => setProject(data),
        () => {
          showToast('project-error', `Could not get ${projectLabels.singular}.`, `${projectLabels.singular} Error`, 'danger');
          setError(true);
        }
      );
    // eslint-disable-next-line
  }, []);

  const getModelAssessmentStatus = useCallback(() => {
    Projects.getModelAssessment(project!.id!)
      .then(
        ({ data }) => {
          setAssessmentStatus(data.status as AssessmentStatus);
          setAssessmentData(data);
          setForcePolling(![AssessmentStatus.COMPLETED, AssessmentStatus.FAILED, AssessmentStatus.NONE].includes(data.status));
        },
        () => {
          showToast('assessment-status-error', `Could not get ${modelLabels.singular} assessment status.`, `Error`, 'danger');
        }
      );
  }, [project, showToast, modelLabels]);

  useEffect(() => {
    if (project && project.models.length > 0 && project.datasets.length > 0) getModelAssessmentStatus();
  }, [project, getModelAssessmentStatus]);

  useInterval(() => getModelAssessmentStatus(), forcePolling ? 10000 : undefined);

  useEffect(() => {
    if (projectUseCase) EventEmitter.dispatch('projectUseCase', projectUseCase);
  }, [projectUseCase]);

  const onEditProject = () => navigate(`/projects/${id}/edit`);
  const onDeleteProject = () => setShowDeleteModal(true);
  const onAssessmentModel = () => setShowAssessmentModal(true);
  const onViewAssessmentData = () => setShowAssessmentDataModal(true);
  const handleDeleteClose = () => setShowDeleteModal(false)
  const handleAssessmentClose = () => setShowAssessmentModal(false);
  const handleAssessmentDataClose = () => setShowAssessmentDataModal(false);

  const deleteProject = () => {
    showLoader();

    Projects.deleteProject(project!.id!)
      .then(
        () => {
          showToast('project-delete-success', `${projectLabels.singular} deleted successfully.`, 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/projects');
        },
        () => {
          showToast('project-delete-error', `Could not delete ${projectLabels.singular}.`, `${projectLabels.singular} Error`, 'danger');
          hideLoader();
        }
      );
  };

  const assessModel = useCallback(() => {
    showLoader();

    setAssessmentData(undefined);

    const body = {
      process_type: ProcessType.FULL_ASSESSMENT,
      assessment_id: project!.id!.toString(),
      user_id: loggedUser!.sub!,
      themis_user_id: loggedUser!.sub! + "",
      tai_characteristics: ["accuracy", "fairness", "robustness"],
      frontend_metadata: {
        tags: project!.tags || [],
      },
    }

    Projects.assessModel(body)
      .then(
        ({ data }) => {
          showToast('assessment-success', `${modelLabels.singular} assessment started successfully.`, 'Success', 'success');
          setShowAssessmentModal(false);
          setForcePolling(true);
          setAssessmentStatus(data.status as AssessmentStatus);
          hideLoader();
        },
        () => {
          showToast('assessment-error', `Could not start ${modelLabels.singular} assessment.`, `Error`, 'danger');
          hideLoader();
        }
      );
  }, [showLoader, hideLoader, showToast, project, modelLabels, loggedUser]);

  const assessmentButtonVisible = useMemo(() => (project?.models || []).length > 0 && (project?.datasets || []).length > 0 && (assessmentStatus || assessmentStatus === AssessmentStatus.NONE), [project, assessmentStatus]);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred during {projectLabels.singular} retrieve. Try again later.</span>
      </Alert>
    );

    if (!project) return (
      <div className="d-flex justify-content-center py-5">
        <AlidaLoader size={300} />
      </div>
    );

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/projects' }}>{projectLabels.plural}</Breadcrumb.Item>
          <Breadcrumb.Item active>{project.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card className="item-detail-group main bg-project">
          <FontAwesomeIcon className="item-icon" icon={solid('folder')} transform="shrink-4" />

          <div className="position-relative">
            <Flex justify="end">
              <Badge className="shadow">{project.accessLevel}</Badge>
            </Flex>

            <div className="p-2">
              <Row className="gx-0 align-items-center justify-content-md-between">
                <Col xs={12} md="auto" className="pe-3">
                  <h3 className="mb-0">{project.name}</h3>
                  <p className="text-trailing mb-3">{project.description}</p>
                </Col>

                <Col xs={12} md="auto" className="text-center text-md-start">
                  <Tooltip title="Edit">
                    <button className="circle-button" onClick={onEditProject}>
                      <FontAwesomeIcon icon={solid('pen')} />
                    </button>
                  </Tooltip>
                  {assessmentButtonVisible && (
                    <Tooltip title={`Assessment${assessmentStatus === AssessmentStatus.RUNNING ? " Running" : ""}`}>
                      <button className="circle-button" disabled={assessmentStatus === AssessmentStatus.RUNNING} onClick={onAssessmentModel}>
                        <FontAwesomeIcon icon={assessmentStatus === AssessmentStatus.RUNNING ? solid('spinner') : solid('pen-nib')} spin={assessmentStatus === AssessmentStatus.RUNNING} />
                      </button>
                    </Tooltip>
                  )}
                  {assessmentData && (
                    <Tooltip title="View Assessment Details">
                      <button className="circle-button" disabled={assessmentStatus === AssessmentStatus.RUNNING} onClick={onViewAssessmentData}>
                        <FontAwesomeIcon icon={solid("eye")} />
                      </button>
                    </Tooltip>
                  )}
                  {TAIUrl && (
                    <Tooltip title="Start Tai Assessment">
                      <a href={TAIUrl} type="button" className="circle-button me-3" target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={solid('link')} />
                      </a>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <button className="circle-button" onClick={onDeleteProject}>
                      <FontAwesomeIcon icon={solid('trash')} />
                    </button>
                  </Tooltip>
                </Col>
              </Row>
            </div>

            <Row className="gx-0 justify-content-between">
              <Col xs="auto">
                <div className="bg-highlight">
                  <UserItem className="negative" user={project.owner!} organization={project.organization!} />
                </div>
              </Col>

              <Flex className="tags-wrapper mt-3">
                {project.tags?.map(tag => <AppTag tag={tag} key={tag} />)}
              </Flex>
            </Row>

            <div className="item-dates">
              <div className="d-inline-block">
                <b>Created:</b>
                <span className="ms-1">{formatDate(project.createDateTime!)}</span>
              </div>
              <div className="d-inline-block ms-2">
                <b>Last Update:</b>
                <span className="ms-1">{formatDate(project.updateDateTime!)}</span>
              </div>
            </div>
          </div>
        </Card>

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('shapes')} />
            <h5 className="d-inline ms-2 mb-0">Components</h5>
          </div>

          <Card.Body>
            <Row>
              {project.bdaApplications.map(app => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={app.id}>
                  <ProjectAsset asset={app} type="application" />
                </Col>
              ))}
              {project.datasets.map(dataset => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={dataset.id}>
                  <ProjectAsset asset={dataset} type="dataset" />
                </Col>
              ))}
              {project.models.map(model => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={model.id}>
                  <ProjectAsset asset={model} type="model" />
                </Col>
              ))}
              {project.services.map(service => (
                <Col xs={12} sm={6} md={4} lg={3} className="p-0 mb-2" key={service.id}>
                  <ProjectAsset asset={service} type="service" />
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>{projectLabels.singular} {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>{projectLabels.singular} Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {projectLabels.singular} <b>{project?.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteProject}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAssessmentModal} onHide={handleAssessmentClose}>
        <Modal.Header closeButton>
          <Modal.Title>{modelLabels.singular} Assessment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to Assess {modelLabels.singular} <b>{project?.models[0]?.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleAssessmentClose}>Close</Button>
          <Button variant="success" size="sm" onClick={assessModel}>Assess</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAssessmentDataModal} onHide={handleAssessmentDataClose}>
        <Modal.Header closeButton>
          <Modal.Title>{modelLabels.singular} Assessment Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Model: <b>{project?.models[0]?.name}</b></p>
          <p>Dataset: <b>{project?.datasets[0]?.name}</b></p>
          <p>Status: <b>{assessmentData?.status}</b></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleAssessmentDataClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}